import { useState, useEffect, useRef } from 'react';
import { Panel, PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { CloseButton } from 'foundations-components/transitional/components/CloseButton';
import { H4 } from 'foundations-components/transitional/components/H4';
import { H7 } from 'foundations-components/transitional/components/H7';
import { HR } from 'foundations-components/transitional/components/HR';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { Form } from 'foundations-components/transitional/components/Form';
import { FloatingAlertList, FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { LeftIcon } from 'foundations-assets/icons/components/LeftIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { ChirpError } from 'rpc-client-utils';
import { useChangePasswordMutation } from '../../__generated__/chirp/com/hubspot/portals/platform/app/rpc/SettingsRpc.hooks';
import { getPortalAccountId, getPortalInstanceId } from '../utils/metadata';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ERROR_ALERT_ID = 'password-reset-error';
const PANEL_WIDTH_BY_BREAKPOINT = {
  mobile: '100%',
  tablet: '80%',
  desktop: 600
};
export function PasswordResetPanel({
  onClose,
  onBack
}) {
  const breakpoint = useBreakpoint();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const previouslyFocusedRef = useRef(document.activeElement);
  useEffect(() => {
    const trigger = previouslyFocusedRef.current;
    return () => {
      requestAnimationFrame(() => {
        trigger === null || trigger === void 0 || trigger.focus();
      });
    };
  }, []);
  const [alertStore] = useState(() => FloatingAlertStore.newFloatingAlertStore());
  const accountId = getPortalAccountId();
  const portalInstanceId = getPortalInstanceId();
  const [changePassword, {
    loading
  }] = useChangePasswordMutation();
  const handleError = error => {
    var _error$networkError$c, _error$networkError, _settingsError$passwo;
    const rpcError = (_error$networkError$c = error === null || error === void 0 || (_error$networkError = error.networkError) === null || _error$networkError === void 0 ? void 0 : _error$networkError.cause) !== null && _error$networkError$c !== void 0 ? _error$networkError$c : error === null || error === void 0 ? void 0 : error.cause;
    const settingsError = rpcError instanceof ChirpError ? rpcError : undefined;
    const passwordErrors = (_settingsError$passwo = settingsError === null || settingsError === void 0 ? void 0 : settingsError.passwordErrors) !== null && _settingsError$passwo !== void 0 ? _settingsError$passwo : [];
    switch (settingsError === null || settingsError === void 0 ? void 0 : settingsError.reason) {
      case 'WRONG_CURRENT_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorWrongCurrentPassword')
        });
        break;
      case 'INVALID_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: passwordErrors.length ? passwordErrors.join(' ') : I18n.text('app.myAccountPopover.settings.security.errorInvalidPassword')
        });
        break;
      case 'PASSWORD_LEAKED':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordLeaked')
        });
        break;
      case 'SAME_PASSWORD':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorSamePassword')
        });
        break;
      case 'PASSWORDS_DO_NOT_MATCH':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordsDoNotMatch')
        });
        break;
      case 'RATE_LIMITED':
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorRateLimited')
        });
        break;
      default:
        alertStore.addAlert({
          id: ERROR_ALERT_ID,
          type: 'danger',
          titleText: I18n.text('app.myAccountPopover.settings.security.errorGeneric')
        });
    }
  };
  const handleSubmit = event => {
    event.preventDefault();
    alertStore.removeAlert(ERROR_ALERT_ID);
    if (!currentPassword || !newPassword || !confirmPassword) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorRequired')
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorPasswordsDoNotMatch')
      });
      return;
    }
    if (!accountId || !portalInstanceId) {
      alertStore.addAlert({
        id: ERROR_ALERT_ID,
        type: 'danger',
        titleText: I18n.text('app.myAccountPopover.settings.security.errorGeneric')
      });
      return;
    }
    changePassword({
      variables: {
        currentPassword,
        newPassword,
        newPasswordConfirm: confirmPassword,
        accountId,
        portalInstanceId
      }
    }).then(() => {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alertStore.addAlert({
        type: 'success',
        titleText: I18n.text('app.myAccountPopover.settings.security.changeSuccess'),
        dismissAutoMs: 5000
      });
    }).catch(handleError);
  };
  return /*#__PURE__*/_jsxs(Panel, {
    width: PANEL_WIDTH_BY_BREAKPOINT[breakpoint],
    children: [/*#__PURE__*/_jsx(CloseButton, {
      onClick: onClose,
      color: "text-core-default"
    }), /*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsxs(PanelSection, {
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {
          alertStore: alertStore,
          use: "contextual"
        }), /*#__PURE__*/_jsx(Button, {
          use: "unstyled",
          onClick: onBack,
          paddingInline: "0",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "center",
            gap: "100",
            inlineSize: "100%",
            justifyContent: "start",
            children: [/*#__PURE__*/_jsx(LeftIcon, {
              size: "100",
              color: "text-core-default"
            }), /*#__PURE__*/_jsx(Paragraph, {
              color: "text-core-default",
              marginBlock: "0",
              typography: "body-100",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "app.myAccountPopover.settings.security.back"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(H4, {
          color: "text-core-default",
          marginBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.changePasswordHeading"
          })
        }), /*#__PURE__*/_jsx(H7, {
          color: "text-core-default",
          marginBlockEnd: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.myAccountPopover.settings.security.changePasswordSubtitle"
          })
        }), /*#__PURE__*/_jsx(HR, {
          distance: "flush"
        }), /*#__PURE__*/_jsxs(Form, {
          onSubmit: handleSubmit,
          children: [/*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.currentPasswordLabel'),
                  children: /*#__PURE__*/_jsx(TextInput, {
                    type: "password",
                    name: "currentPassword",
                    value: currentPassword,
                    onChange: event => setCurrentPassword(event.target.value),
                    typography: "body-100"
                  })
                })
              })
            }), /*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.newPasswordLabel'),
                  children: /*#__PURE__*/_jsx(TextInput, {
                    type: "password",
                    name: "newPassword",
                    value: newPassword,
                    onChange: event => setNewPassword(event.target.value),
                    typography: "body-100"
                  })
                })
              })
            }), /*#__PURE__*/_jsx(View, {
              marginBlockEnd: "300",
              children: /*#__PURE__*/_jsx(Fieldset, {
                _size: "small",
                children: /*#__PURE__*/_jsx(FormControl, {
                  label: unescapedText('app.myAccountPopover.settings.security.confirmPasswordLabel'),
                  children: /*#__PURE__*/_jsx(TextInput, {
                    type: "password",
                    name: "confirmPassword",
                    value: confirmPassword,
                    onChange: event => setConfirmPassword(event.target.value),
                    typography: "body-100"
                  })
                })
              })
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            justifyContent: "end",
            gap: "200",
            marginBlockStart: "300",
            children: [/*#__PURE__*/_jsx(Button, {
              use: "secondary-ghost",
              type: "button",
              onClick: onBack,
              children: /*#__PURE__*/_jsx(Paragraph, {
                color: "text-core-default",
                marginBlock: "0",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.myAccountPopover.settings.security.cancel"
                })
              })
            }), /*#__PURE__*/_jsx(Button, {
              use: "primary",
              type: "submit",
              disabled: loading,
              children: loading ? /*#__PURE__*/_jsx(LoadingSpinner, {
                size: "xs"
              }) : /*#__PURE__*/_jsx(Paragraph, {
                color: "text-core-default",
                marginBlock: "0",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "app.myAccountPopover.settings.security.updatePassword"
                })
              })
            })]
          })]
        })]
      })
    })]
  });
}